import { SegmentedControl, Text, Select, Collapse, Avatar, HoverCard } from '@mantine/core';
import type { AutoHealConfig, TestStepMode } from '@prisma/client';
import type { SelectedStep } from '~/server/logic/prisma/step-select';
import { AUTO_HEAL_CONFIG_OPTIONS, AUTO_HEAL_EXPLANATIONS, STEP_MODES } from '~/common/ai/constants';
import { IconQuestionMark } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { api } from '~/trpc-extra/api';

const stepModeOptions: { label: React.ReactNode; value: TestStepMode }[] = STEP_MODES.map(({ label, value }) => ({
  label,
  value,
}));

const MODE_EXPLANATIONS = STEP_MODES.map(({ label, explanation }) => [label, explanation]);

export default function StepMode({
  step,
  disabled,
  isInitialNavigationStep,
  projectAutoHealConfig,
}: {
  step: SelectedStep;
  disabled?: boolean;
  isInitialNavigationStep?: boolean;
  projectAutoHealConfig?: AutoHealConfig;
}) {
  const trpcUtils = api.useUtils();

  const { mutate: updateStep } = api.testStep.update.useMutation({
    onSuccess: () => {
      notifications.show({
        message: 'Step mode updated',
      });
    },
    onError: () => {
      notifications.show({
        message: 'Failed to update step mode',
      });
    },
    onSettled: async () => {
      await trpcUtils.testContent.get.invalidate().catch(() => undefined);
    },
  });

  const selectedProjectAutoHealConfig = projectAutoHealConfig ?? 'UI';

  return (
    <div className="mb-3 flex flex-col space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="text-sm font-medium">Step mode</div>
          <HoverCard
            position="top"
            withArrow
            width={300}
          >
            <HoverCard.Target>
              <Avatar
                size="xs"
                radius="xl"
              >
                <IconQuestionMark size={12} />
              </Avatar>
            </HoverCard.Target>
            <HoverCard.Dropdown>
              <div className="space-y-2">
                {MODE_EXPLANATIONS.map(([mode, explanation]) => (
                  <div key={mode}>
                    <Text
                      size="sm"
                      fw={700}
                    >
                      {mode}
                    </Text>
                    <Text size="xs">{explanation}</Text>
                  </div>
                ))}
              </div>
            </HoverCard.Dropdown>
          </HoverCard>
        </div>
      </div>

      <SegmentedControl
        disabled={disabled || isInitialNavigationStep}
        defaultValue={step.mode}
        data={stepModeOptions}
        onChange={(value) => {
          updateStep({ stepId: step.id, data: { mode: value as TestStepMode } });
        }}
      />

      {!!isInitialNavigationStep && (
        <Text className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Initial navigation step can only be in strict mode.
        </Text>
      )}

      {/* Auto-heal sensitivity dropdown */}
      <Collapse
        in={step.mode === 'AI_FALLBACK'}
        className="w-full"
      >
        <div
          className="w-full"
          // Prevents closing parent popover when selecting an option
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <div className="mt-1 flex flex-col space-y-2">
            <Text className="text-sm font-medium">Auto-heal sensitivity</Text>
            <Select
              value={step.autoHealConfig}
              data={AUTO_HEAL_CONFIG_OPTIONS}
              onChange={(selectedValue) => {
                if (selectedValue) {
                  updateStep({ stepId: step.id, data: { autoHealConfig: selectedValue as AutoHealConfig } });
                } else {
                  updateStep({ stepId: step.id, data: { autoHealConfig: null } });
                }
              }}
              allowDeselect={true}
              clearable
              placeholder={`Default (${AUTO_HEAL_CONFIG_OPTIONS.find((option) => option.value === selectedProjectAutoHealConfig)?.label})`}
              size="sm"
              comboboxProps={{ width: 250 }}
              renderOption={({ option }) => (
                <div className="flex flex-nowrap gap-2">
                  <div>
                    <Text className="text-sm">{option.label}</Text>
                    <Text className="text-[11px] text-gray-500">
                      {AUTO_HEAL_EXPLANATIONS[option.value as AutoHealConfig]}
                    </Text>
                  </div>
                </div>
              )}
            />
          </div>
        </div>
      </Collapse>
    </div>
  );
}
